/**
 * i18n injection point for the dashboard-render package.
 *
 * The package is consumed by two apps (ai-reporting-builder + hp-portal)
 * that may initialize ``react-i18next`` with different resource dicts.
 * Rather than importing ``react-i18next`` directly inside helper code
 * (which would couple us to the timing of the consumer's own
 * ``i18n.init()`` call), the consumer wires its initialized instance
 * here once, and downstream helpers read ``getI18n().language`` to make
 * locale-aware decisions (e.g. ``formatCellValue`` resolving a
 * bilingual JSON cell).
 *
 * React components inside the package continue to use the standard
 * ``useTranslation()`` hook from ``react-i18next`` for ``t()`` and
 * subscription semantics — this binding is only for the non-React
 * code paths (formatters, value coercers, plain helpers).
 *
 * Wiring (do this once in each consumer's ``main.tsx`` after
 * ``i18n.init()``):
 *
 *     import i18n from "./i18n";
 *     import { setI18nInstance } from "@pervasivemind/dashboard-render";
 *     setI18nInstance(i18n);
 */

import { useTranslation } from "react-i18next";

/**
 * Structural type for the i18next instance the consumer wires in.
 *
 * We do NOT ``import type { i18n } from "i18next"`` here on purpose.
 * Each consumer pins its own ``i18next`` version, and node module
 * resolution sees two copies (workspace root + consumer-local). The
 * generated TFunction generics drift between minor releases (26.0 vs
 * 26.2 differ on the ``TemplateStringsArray`` arg), which made the
 * official type incompatible at the ``setI18nInstance`` call site.
 *
 * The minimal surface we actually need is ``.language`` (read) and
 * ``.changeLanguage()`` (write) plus a few subscription helpers. The
 * structural shape below covers those without locking to a specific
 * i18next version.
 */
export interface I18nLike {
  language: string;
}

let _i18n: I18nLike | null = null;

/**
 * Wire the consumer's ``react-i18next`` instance. Idempotent — calling
 * twice with the same instance is a no-op; calling with a different
 * instance overwrites (useful in tests).
 */
export function setI18nInstance(instance: I18nLike): void {
  _i18n = instance;
}

/**
 * Read the wired instance. Throws if ``setI18nInstance`` was never
 * called — the error surfaces immediately during dev, rather than
 * silently rendering with the wrong locale.
 */
export function getI18n(): I18nLike {
  if (_i18n == null) {
    throw new Error(
      "@pervasivemind/dashboard-render: setI18nInstance(i18n) must be " +
        "called once at app boot (in main.tsx, after i18n.init()) " +
        "before any render-layer code runs.",
    );
  }
  return _i18n;
}

/** The UI languages the product ships, in selector order. */
export const LANGS = ["en", "es", "pt"] as const;

export type Lang = (typeof LANGS)[number];

/**
 * Narrow any i18next language tag (``es-CL``, ``pt-BR``, ``en``) to a
 * ``Lang`` by its two-letter prefix. A tag outside ``LANGS`` reads as
 * ``"en"`` — the same answer this package gave before PT existed, and
 * the i18next ``fallbackLng`` of both apps.
 */
export function toLang(raw: string | null | undefined): Lang {
  const prefix = (raw ?? "").slice(0, 2).toLowerCase();
  return (LANGS as readonly string[]).includes(prefix) ? (prefix as Lang) : "en";
}

/**
 * Pick the copy for ``lang`` out of a per-language table. For the few
 * strings that live in code instead of an app dictionary (helpers that
 * have the language but no ``t``). Every ``Lang`` is required by the
 * type, so a new language cannot be added without its copy.
 */
export function pickLang<T>(lang: Lang, byLang: Record<Lang, T>): T {
  return byLang[lang];
}

/**
 * Convenience: read the active locale as a ``Lang``.
 * Components inside the package call this from non-React helpers
 * (cell formatters etc.) where ``useTranslation`` is unavailable.
 */
export function getActiveLocale(): Lang {
  try {
    return toLang(getI18n().language || "en");
  } catch {
    // Pre-bind safety: most call sites are inside render code that
    // runs after main.tsx, but a few helpers can be invoked from
    // module-init paths during HMR. Default to EN rather than
    // throwing a noisy error and breaking the render entirely.
    return "en";
  }
}

/**
 * React hook variant of ``getActiveLocale`` — subscribes to the
 * react-i18next instance so components re-render on language change.
 * Use this inside components; use ``getActiveLocale`` for non-React
 * call sites.
 */
export function useLang(): Lang {
  const { i18n } = useTranslation();
  return toLang(i18n.language);
}
